import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Settings,
  User,
  FolderKanban,
  Sparkles,
  Quote,
  Inbox,
  KeyRound,
  LogOut,
  Menu,
  X,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/site", label: "Site Info", icon: Settings },
  { to: "/admin/about", label: "About", icon: User },
  { to: "/admin/projects", label: "Projects", icon: FolderKanban },
  { to: "/admin/services", label: "Services", icon: Sparkles },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote },
  { to: "/admin/messages", label: "Messages", icon: Inbox },
  { to: "/admin/account", label: "Account", icon: KeyRound },
];

const AdminLayout = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" style={{ backgroundColor: "var(--bg-2)" }}>
      <aside
        className={`md:w-72 md:shrink-0 md:sticky md:top-0 md:h-screen md:flex md:flex-col bg-[color:var(--ink)] text-[color:var(--bg)] ${
          open ? "block" : "hidden md:flex"
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-2">
            <span className="relative w-9 h-9 rounded-full bg-[color:var(--bg)] text-[color:var(--ink)] font-display font-bold grid place-items-center">
              m
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[color:var(--coral)]" />
            </span>
            <span className="font-display text-xl font-semibold">Misbah CMS</span>
          </Link>
          <button className="md:hidden" onClick={() => setOpen(false)} aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[color:var(--coral)] text-white"
                    : "text-[color:var(--bg)]/85 hover:bg-white/10"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-3">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-sm text-[color:var(--bg)]/80 hover:text-white"
          >
            <ExternalLink className="w-4 h-4" /> View live site
          </a>
          <div className="text-xs text-[color:var(--bg)]/60 truncate">{admin?.email}</div>
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="md:hidden flex items-center justify-between p-4 border-b border-black/10 bg-[color:var(--bg)]">
          <button onClick={() => setOpen(true)} aria-label="Open menu">
            <Menu className="w-5 h-5" />
          </button>
          <span className="font-display text-lg font-semibold">Misbah CMS</span>
          <button onClick={onLogout} aria-label="Sign out">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
        <main className="flex-1 p-5 md:p-10 max-w-5xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
